import { Federation } from '@prisma/client'
import { z } from 'zod'
import { LicenseOnly } from 'domain/models/AttendeeFormSchema/AccessRule'
import { UploadedDocumentKeySchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/UploadedDocumentKeySchema'

const CompleteSecondaryFederationLicenseSchema = (
  secondaryFederations: [Federation, ...Federation[]]
) => {
  return z.object({
    completeType: z.literal('LICENSE_MANUALLY_VERIFIED'),
    federationName: z
      .string()
      .min(1)
      .pipe(
        z.enum([secondaryFederations[0], ...secondaryFederations.slice(1)])
      ),
    documentNumber: z.string().min(1),
    documentVerification: ManuallyVerifiedLicenseSchema
  })
}

const ManuallyVerifiedLicenseSchema = z.object({
  verificationStatus: z.literal('MANUALLY_VERIFIED'),
  uploadedDocumentKey: UploadedDocumentKeySchema
})

const VerifiedLicenseSchema = z.object({
  verificationStatus: z.literal('VERIFIED'),
  verifiedAt: z.coerce.date()
})

const UnverifiedLicenseSchema = z.object({
  verificationStatus: z.literal('UNVERIFIED'),
  uploadedDocumentKey: UploadedDocumentKeySchema
})

const NotVerifiedLicenseSchema = z.object({
  verificationStatus: z.enum(['INVALID', 'REQUIRES_RENEWAL']),
  uploadedDocumentKey: UploadedDocumentKeySchema,
  verifiedAt: z.coerce.date()
})

const LicenseVerificationSchema = z.discriminatedUnion('verificationStatus', [
  VerifiedLicenseSchema,
  NotVerifiedLicenseSchema,
  UnverifiedLicenseSchema
])

const CompletePrimaryFederationLicenseSchema = (
  primaryFederation: Federation
) => {
  return z.object({
    completeType: z.literal('LICENSE_AUTO_VERIFIED'),
    federationName: z.string().min(1).pipe(z.literal(primaryFederation)),
    documentNumber: z.string().min(1),
    documentVerification: LicenseVerificationSchema
  })
}

export const CompleteLicenseNowSchema = (
  accessRule: Omit<LicenseOnly, 'type'>
) => {
  const secondaryFederations = accessRule.acceptedFederations.filter(
    (f) => f !== accessRule.specialFederationWithId?.name
  )
  const SecondaryFederationSchema = secondaryFederations[0]
    ? CompleteSecondaryFederationLicenseSchema([
        secondaryFederations[0],
        ...secondaryFederations.slice(1)
      ])
    : undefined
  const PrimaryFederationSchema = accessRule.specialFederationWithId
    ? CompletePrimaryFederationLicenseSchema(
        accessRule.specialFederationWithId.name
      )
    : undefined

  return SecondaryFederationSchema && PrimaryFederationSchema
    ? ([PrimaryFederationSchema, SecondaryFederationSchema] as const)
    : SecondaryFederationSchema
      ? ([SecondaryFederationSchema] as const)
      : PrimaryFederationSchema
        ? ([PrimaryFederationSchema] as const)
        : ([
            CompleteSecondaryFederationLicenseSchema(
              accessRule.acceptedFederations
            )
          ] as const)
}

export const makeCompleteLicenseNowSchema = (accessRule: LicenseOnly) =>
  z.object({
    completeLater: z.literal(false),
    document: z.discriminatedUnion('completeType', [
      ...CompleteLicenseNowSchema(accessRule)
    ])
  })
